import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  App as AntApp, Button, DatePicker, Form, Input, InputNumber, Select, Switch,
} from "antd";
import {
  CalendarOutlined,
  FileTextOutlined,
  NumberOutlined,
  PlayCircleOutlined,
  ReloadOutlined,
  RightOutlined,
  SlidersOutlined,
  TagOutlined,
  ThunderboltOutlined,
} from "@ant-design/icons";
import dayjs, { type Dayjs } from "dayjs";

import { api, ApiError } from "../api";
import { ScraperDot } from "../components/ScraperTag";
import type { InputField, ScraperSchema } from "../types";

const LAST_SCRAPER_KEY = "crl:lastScraper";
const INPUT_KEY = (name: string) => `crl:input:${name}`;

export function ScraperPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { message } = AntApp.useApp();
  const [schemas, setSchemas] = useState<ScraperSchema[] | null>(null);
  const [selected, setSelected] = useState<string>("");
  const [form] = Form.useForm();
  const [bypassCache, setBypassCache] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.scrapers()
      .then(({ scrapers }) => {
        setSchemas(scrapers);
        const fromQuery = searchParams.get("s");
        const saved = localStorage.getItem(LAST_SCRAPER_KEY);
        const target = fromQuery ?? saved;
        const initial = scrapers.find((s) => s.name === target) ?? scrapers[0];
        if (initial) setSelected(initial.name);
      })
      .catch((err) => message.error(err instanceof Error ? err.message : "Failed to load scrapers"));
    // Re-run when the ?s= query param changes so sidebar clicks switch scrapers.
  }, [message, searchParams]);

  const currentSchema = useMemo(
    () => schemas?.find((s) => s.name === selected) ?? null,
    [schemas, selected],
  );

  useEffect(() => {
    if (!currentSchema) return;
    localStorage.setItem(LAST_SCRAPER_KEY, currentSchema.name);
    const saved = localStorage.getItem(INPUT_KEY(currentSchema.name));
    const initial = saved ? safeParse(saved) : defaultsFor(currentSchema);
    form.resetFields();
    form.setFieldsValue(initial);
  }, [currentSchema, form]);

  async function handleFinish(values: Record<string, unknown>) {
    if (!currentSchema) return;
    localStorage.setItem(INPUT_KEY(currentSchema.name), JSON.stringify(values));
    const payload = cleanPayload(values, currentSchema);
    setSubmitting(true);
    try {
      const { id } = await api.runScraper(currentSchema.name, payload, bypassCache);
      navigate(`/output/${id}`);
    } catch (err) {
      message.error(err instanceof ApiError ? err.message : err instanceof Error ? err.message : "Run failed");
    } finally {
      setSubmitting(false);
    }
  }

  function handleReset() {
    if (!currentSchema) return;
    form.setFieldsValue(defaultsFor(currentSchema));
    setBypassCache(false);
  }

  if (!schemas)
    return (
      <div style={{ padding: 40, textAlign: "center", color: "var(--text-tertiary)" }}>
        Loading scrapers…
      </div>
    );

  return (
    <>
      <div className="crl-page-top">
        <div className="crl-breadcrumb">
          <span>Workspace</span>
          <RightOutlined className="crl-chev" style={{ fontSize: 10 }} />
          <span className="crl-cur">New run</span>
        </div>
        <div className="crl-page-top-spacer" />
      </div>

      <div className="crl-page-header">
        <h1 className="crl-page-title">
          <span className="crl-title-icon">
            <ThunderboltOutlined style={{ fontSize: 15 }} />
          </span>
          New scraping run
        </h1>
        <div className="crl-page-sub">
          Configure a scraper and dispatch a job to the queue.
        </div>
      </div>

      <div className="crl-form-layout">
        <div className="crl-form-card">
          <div className="crl-sp-picker">
            {schemas.map((s) => (
              <button
                key={s.name}
                type="button"
                className={`crl-sp-btn${s.name === selected ? " active" : ""}`}
                onClick={() => setSelected(s.name)}
              >
                <ScraperDot name={s.name} />
                {s.label}
              </button>
            ))}
          </div>

          {currentSchema && (
            <Form
              form={form}
              onFinish={handleFinish}
              disabled={submitting}
              component={false}
            >
              {groupFields(currentSchema.fields).map((group) => (
                <div key={group.key}>
                  <div className="crl-form-section-title">{group.title}</div>
                  {group.key === "range" ? (
                    <DateRangeBlock form={form} />
                  ) : (
                    group.fields.map((f) => (
                      <div className="crl-form-row" key={f.key}>
                        <div className="crl-lbl-row">
                          {fieldIcon(f)}
                          {f.label}
                          {fieldRequired(f) && <span className="crl-req">*</span>}
                        </div>
                        <div className="crl-val">{renderField(f)}</div>
                      </div>
                    ))
                  )}
                </div>
              ))}
              <div className="crl-form-section-title">Options</div>
              <div className="crl-form-row">
                <div className="crl-lbl-row">
                  <ReloadOutlined />
                  Bypass 24h cache
                </div>
                <div className="crl-val">
                  <Switch
                    size="small"
                    checked={bypassCache}
                    onChange={setBypassCache}
                  />
                </div>
              </div>

              <div className="crl-form-actions">
                <Button
                  type="primary"
                  icon={<PlayCircleOutlined />}
                  loading={submitting}
                  onClick={() => form.submit()}
                >
                  Run scraper
                </Button>
                <Button onClick={handleReset} disabled={submitting}>
                  Reset
                </Button>
                <div className="crl-spacer" />
                {currentSchema.description && (
                  <span className="crl-form-hint">{currentSchema.description}</span>
                )}
              </div>
            </Form>
          )}
        </div>
      </div>
    </>
  );
}

/** Replaces the three awkward date fields (from_date / to_date / since_days)
 *  with preset pills ("Last 7 days", "Last 14 days", …) plus an optional
 *  custom range-picker. Writes back to the same form keys the backend expects
 *  so the payload-shaping logic stays untouched. */
const DATE_PRESETS: Array<{ days: number; label: string }> = [
  { days: 7,  label: "Last 7 days" },
  { days: 14, label: "Last 14 days" },
  { days: 30, label: "Last 30 days" },
  { days: 90, label: "Last 90 days" },
];

function DateRangeBlock({ form }: { form: ReturnType<typeof Form.useForm>[0] }) {
  // We read raw form values each render via Form.useWatch so the UI reflects
  // whatever's currently set (including after Reset or initial load).
  const since = Form.useWatch<number | undefined>("since_days", form);
  const fromStr = Form.useWatch<string | undefined>("from_date", form);
  const toStr = Form.useWatch<string | undefined>("to_date", form);
  const mode: "preset" | "custom" = fromStr || toStr ? "custom" : "preset";
  const from = fromStr ? dayjs(fromStr) : null;
  const to = toStr ? dayjs(toStr) : null;

  function applyPreset(days: number | null) {
    form.setFieldsValue({
      since_days: days ?? undefined,
      from_date: "",
      to_date: "",
    });
  }
  function applyCustom(range: [Dayjs | null, Dayjs | null] | null) {
    const f = range?.[0];
    const t = range?.[1];
    form.setFieldsValue({
      since_days: undefined,
      from_date: f ? f.startOf("day").toISOString() : "",
      to_date: t ? t.endOf("day").toISOString() : "",
    });
  }
  function clearAll() {
    form.setFieldsValue({ since_days: undefined, from_date: "", to_date: "" });
  }

  return (
    <div className="crl-form-row" style={{ alignItems: "flex-start", padding: "10px 14px" }}>
      <div className="crl-lbl-row">
        <CalendarOutlined />
        When
      </div>
      <div className="crl-val" style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {DATE_PRESETS.map((p) => (
            <button
              key={p.days}
              type="button"
              className={`crl-sp-btn${mode === "preset" && since === p.days ? " active" : ""}`}
              onClick={() => applyPreset(p.days)}
              style={{ padding: "4px 10px", fontSize: 12 }}
            >
              {p.label}
            </button>
          ))}
          <button
            type="button"
            className={`crl-sp-btn${mode === "custom" ? " active" : ""}`}
            onClick={() => applyCustom([dayjs().subtract(7, "day"), dayjs()])}
            style={{ padding: "4px 10px", fontSize: 12 }}
          >
            Custom range
          </button>
          {(since || fromStr || toStr) && (
            <button
              type="button"
              className="crl-sp-btn"
              onClick={clearAll}
              style={{ padding: "4px 10px", fontSize: 12 }}
            >
              Clear
            </button>
          )}
        </div>
        {mode === "custom" && (
          <DatePicker.RangePicker
            value={[from, to]}
            onChange={(range) => applyCustom(range as [Dayjs | null, Dayjs | null] | null)}
            allowClear
            style={{ width: "100%", maxWidth: 360 }}
          />
        )}
      </div>
      {/* Keep the real form fields mounted (hidden) so the payload shape still
          carries from_date/to_date/since_days to the backend. */}
      <Form.Item name="from_date" noStyle><Input type="hidden" /></Form.Item>
      <Form.Item name="to_date" noStyle><Input type="hidden" /></Form.Item>
      <Form.Item name="since_days" noStyle><Input type="hidden" /></Form.Item>
    </div>
  );
}

/** Group a scraper's input fields into named sections so the form doesn't
 *  read as one flat list. Section membership is decided by the field key:
 *  SCOPE picks what to scrape (mode, items, categories, brands),
 *  RANGE is the date window (reamaze only), LIMITS caps the run size,
 *  LOOKUP is the single-SKU / single-slug entry used in sku mode. */
interface FieldGroup { key: string; title: string; fields: InputField[] }

function groupFields(fields: InputField[]): FieldGroup[] {
  const rangeKeys = new Set(["from_date", "to_date", "since_days"]);
  const limitKeys = new Set(["max_pages", "max_guides", "max_conversations", "min_messages"]);
  const scopeKeys = new Set(["mode", "items", "categories", "brands", "brand", "brand_path", "scope"]);

  const scope: InputField[] = [];
  const range: InputField[] = [];
  const limits: InputField[] = [];
  const lookup: InputField[] = [];

  for (const f of fields) {
    if (f.key === "sku") lookup.push(f);
    else if (rangeKeys.has(f.key)) range.push(f);
    else if (limitKeys.has(f.key)) limits.push(f);
    else if (scopeKeys.has(f.key)) scope.push(f);
    else scope.push(f);  // unknown keys land in Scope as a safe default.
  }

  const groups: FieldGroup[] = [];
  if (scope.length)  groups.push({ key: "scope",  title: "Scope",        fields: scope  });
  if (range.length)  groups.push({ key: "range",  title: "Date range",   fields: range  });
  if (limits.length) groups.push({ key: "limits", title: "Limits",       fields: limits });
  if (lookup.length) groups.push({ key: "lookup", title: "Single lookup", fields: lookup });
  return groups;
}

function fieldIcon(f: InputField): React.ReactNode {
  switch (f.type) {
    case "text":
    case "textarea":
      return <FileTextOutlined />;
    case "number":
      return <NumberOutlined />;
    case "switch":
      return <SlidersOutlined />;
    case "select":
      return <SlidersOutlined />;
    case "multiSelect":
      return <TagOutlined />;
    default:
      return <CalendarOutlined />;
  }
}

function fieldRequired(f: InputField): boolean {
  return "required" in f && !!f.required;
}

function renderField(f: InputField): React.ReactNode {
  switch (f.type) {
    case "text":
      return (
        <Form.Item
          name={f.key}
          noStyle
          rules={f.required ? [{ required: true, message: `${f.label} required` }] : undefined}
        >
          <Input placeholder={f.placeholder} variant="borderless" />
        </Form.Item>
      );
    case "textarea":
      return (
        <Form.Item name={f.key} noStyle>
          <Input.TextArea rows={f.rows ?? 3} placeholder={f.placeholder} />
        </Form.Item>
      );
    case "number":
      return (
        <Form.Item name={f.key} noStyle>
          <InputNumber min={f.min} max={f.max} style={{ width: "100%" }} variant="borderless" />
        </Form.Item>
      );
    case "switch":
      return (
        <Form.Item name={f.key} valuePropName="checked" noStyle>
          <Switch size="small" />
        </Form.Item>
      );
    case "select":
      return (
        <Form.Item name={f.key} noStyle>
          <Select options={f.options} variant="borderless" style={{ width: "100%" }} />
        </Form.Item>
      );
    case "multiSelect":
      return (
        <Form.Item name={f.key} noStyle>
          <Select
            mode={f.allowCustom ? "tags" : "multiple"}
            showSearch
            allowClear
            placeholder={f.placeholder ?? "All"}
            options={f.options}
            optionFilterProp="label"
            tokenSeparators={[","]}
            variant="borderless"
            style={{ width: "100%" }}
          />
        </Form.Item>
      );
  }
}

function defaultsFor(schema: ScraperSchema): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const f of schema.fields) {
    switch (f.type) {
      case "text":
      case "textarea":
      case "select":
        out[f.key] = f.default ?? "";
        break;
      case "number":
        out[f.key] = f.default ?? undefined;
        break;
      case "switch":
        out[f.key] = f.default ?? false;
        break;
      case "multiSelect":
        out[f.key] = [];
        break;
    }
  }
  return out;
}

function safeParse(s: string): Record<string, unknown> {
  try {
    const v = JSON.parse(s);
    return typeof v === "object" && v ? (v as Record<string, unknown>) : {};
  } catch {
    return {};
  }
}

function cleanPayload(input: Record<string, unknown>, schema: ScraperSchema): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const f of schema.fields) {
    const v = input[f.key];
    if (v === undefined || v === null || v === "") continue;
    if (Array.isArray(v) && v.length === 0) continue;
    out[f.key] = v;
  }
  return out;
}
